import { useMemo, useState, useEffect } from 'react';
import useSWR from 'swr';
import {
 Stack,
 Button,
 Heading,
 HStack,
 Center,
 Container,
 useToast,
 Flex,
 Text,
 Box,
} from '@chakra-ui/react';
import { InputText, Select, YearPickerInput } from '../../components/Input';
import Loader from '../../components/Feedback/Loader';
import { QuestionnaireHeaderNew } from '../../components/Form';
import { fetchData } from '../../services/fetch';
import { useForm } from 'react-hook-form';
import baseService from '../../services/base';
import { SuccessModal } from '../../components/Modals';
import { useNavigate } from 'react-router-dom';
import ButtonOnlyIcon from '../../components/Button/Only-icon';
import SealAssetInput from '../../components/Certification/SealAssetInput';
import MonthRangeFields from '../../components/Certification/MonthRangeFields';
import { SEAL_MODELS } from '../../resources/sealModels';
import { monthToStartDate, monthToEndDate } from '../../utils/monthRange';

export default function CreateCertification() {
 const toast = useToast();
 const navigate = useNavigate();

 const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
 const [chosenQuestionnaire, setChosenQuestionnaire] = useState(null);
 const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
 const [certificationName, setCertificationName] = useState('');
 const [certificationExercise, setCertificationExercise] = useState(null);
 const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
 const [cutoffScores, setCutoffScores] = useState({});
 // EG-23/EG-24/EG-27: selo, 2ª imagem e guia opcionais na criação ({ base64, filename }).
 const [seal, setSeal] = useState(null);
 const [seal2, setSeal2] = useState(null);
 const [guide, setGuide] = useState(null);
 // EG-27 (rework): tipo por selo + toggle do 2º selo.
 const [sealModel, setSealModel] = useState('ESG');
 const [sealModel2, setSealModel2] = useState('ESG');
 const [seal2Enabled, setSeal2Enabled] = useState(false);
 // EG-27 (rework): janela de validade mês/ano (YYYY-MM) POR SELO.
 const [validFromMonth, setValidFromMonth] = useState('');
 const [validUntilMonth, setValidUntilMonth] = useState('');
 const [validFromMonth2, setValidFromMonth2] = useState('');
 const [validUntilMonth2, setValidUntilMonth2] = useState('');

 const { data: questionnaires, isLoading } = useSWR('/survey/certification', fetchData);
 const { data: defaultAssets } = useSWR('/certificate/assets/default', fetchData);
 // EG-27: arte por modelo (p/ a prévia do selo refletir o tipo escolhido).
 const { data: sealModelsData } = useSWR('/certificate/seal-models', fetchData);
 const modelUrl = (m) =>
  (Array.isArray(sealModelsData) ? sealModelsData.find((x) => x.model === m) : null)
   ?.url || null;

 const {
  handleSubmit,
  formState: { isSubmitting },
 } = useForm();

 const questionnaireOptions = useMemo(() => {
  return (
   questionnaires?.map((questionnaire) => ({
    label: questionnaire.description,
    value: questionnaire.id_survey,
   })) || []
  );
 }, [questionnaires]);

 const handleQuestionnaireSelect = (value) => {
  setSelectedQuestionnaire(value);
 };

 const handleAddQuestionnaire = () => {
  const selectedData = questionnaires.find(
   (q) => q.id_survey === Number(selectedQuestionnaire)
  );
  setChosenQuestionnaire(selectedData);
  setSelectedQuestionnaire(null);

  if (selectedData.has_pillar) {
   const initialScores = selectedData?.pillars?.reduce((acc, pillar) => {
    acc[pillar?.id_pillar] = '';
    return acc;
   }, {});
   setCutoffScores(initialScores);
  } else {
   setCutoffScores({ main: '' });
  }
 };

 const handleRemoveQuestionnaire = () => {
  setChosenQuestionnaire(null);
  setSelectedQuestionnaire(null);
  setCutoffScores({});
 };

 const handleNameChange = (e) => {
  setCertificationName(e.target.value);
 };

 const handleExerciseChange = (date) => {
  setCertificationExercise(date);
 };

 const handleCutoffScoreChange = (value, pillarId = 'main') => {
  setCutoffScores((prev) => ({
   ...prev,
   [pillarId]: value,
  }));
 };

 useEffect(() => {
  const areCutoffScoresFilled = Object.values(cutoffScores).every(
   (score) => score !== '' && score !== null && score !== undefined
  );
  const areAllPillarsFilled = chosenQuestionnaire?.has_pillar
   ? chosenQuestionnaire.pillars?.every(
      (pillar) =>
       cutoffScores[pillar?.id_pillar] !== '' &&
       cutoffScores[pillar?.id_pillar] !== null &&
       cutoffScores[pillar?.id_pillar] !== undefined
     )
   : cutoffScores.main !== '' &&
     cutoffScores.main !== null &&
     cutoffScores.main !== undefined;

  setIsSubmitEnabled(
   !!certificationName &&
    !!certificationExercise &&
    !!chosenQuestionnaire &&
    areCutoffScoresFilled &&
    areAllPillarsFilled
  );
 }, [certificationName, certificationExercise, chosenQuestionnaire, cutoffScores]);

 const showErrorToast = (message) => {
  toast({
   title: 'Erro',
   description: message,
   status: 'error',
  });
 };

 const closeSuccessModal = () => {
  setIsSubmitSuccessful(false);
  navigate('/certifications');
 };

 const onSubmit = async (data) => {
  try {
   const payload = {
    name: certificationName,
    exercise: certificationExercise ? certificationExercise.getFullYear() : undefined,
    linkCertificates: [chosenQuestionnaire?.id_survey],
    seal_model: sealModel,
    seal_2_enabled: seal2Enabled,
    ...(seal2Enabled && { seal_model_2: sealModel2 }),
    valid_from: validFromMonth ? monthToStartDate(validFromMonth) : null,
    valid_until: validUntilMonth ? monthToEndDate(validUntilMonth) : null,
    valid_from_2:
     seal2Enabled && validFromMonth2 ? monthToStartDate(validFromMonth2) : null,
    valid_until_2:
     seal2Enabled && validUntilMonth2 ? monthToEndDate(validUntilMonth2) : null,
   };

   if (chosenQuestionnaire?.has_pillar) {
    payload.pillar_scores = chosenQuestionnaire?.pillars?.map((pillar) => ({
     id_pillar: pillar?.id_pillar,
     score: Number(cutoffScores[pillar?.id_pillar]),
    }));
   } else {
    payload.cutoff_score = Number(cutoffScores.main);
   }

   const response = await baseService.post(`certificate`, payload);

   // EG-23/EG-24: sobe selo/guia na certificação recém-criada, se anexados.
   const newId = response?.data?.certificate?.id;
   if ((seal || (seal2Enabled && seal2) || guide) && newId) {
    await baseService.patch(`certificate/${newId}/assets`, {
     ...(seal && { seal }),
     ...(seal2Enabled && seal2 && { seal2 }),
     ...(guide && { guide }),
    });
   }

   setIsSubmitSuccessful(true);
  } catch (error) {
   if (error?.response?.status === 400 && error?.response?.data?.message) {
    showErrorToast(error.response.data.message);
   } else {
    showErrorToast('Não foi possível salvar a certificação');
   }
  }
 };
 
 if (isLoading) {
  return <Loader />;
 }

 return (
  <form onSubmit={handleSubmit(onSubmit)}>
   <QuestionnaireHeaderNew
    heading='Cadastrar Certificação'
    isSubmitting={isSubmitting}
    isSubmitEnabled={isSubmitEnabled}
    isSubmit={true}
   >
    <InputText
     placeholder='Digite o nome da certificação'
     autoComplete='off'
     width='480px'
     maxW='90%'
     onChange={handleNameChange}
    />
    <YearPickerInput
     value={certificationExercise}
     onChange={handleExerciseChange}
     placeholder='Exercício'
    />
   </QuestionnaireHeaderNew>

   <Center my={['8px', '24px']} px={{ xl: '86px', '2xl': 'unset' }}>
    <Container maxW={'1280px'} p={0} minH='80vh'>
     <Stack bg='white' spacing='24px' p='6' borderRadius='12px'>
      <Box>
       <Heading as='h3' fontSize='md' fontWeight={600}>
        Selos e guia de uso
       </Heading>
       <Text fontSize='sm' color='#5C6066' mt='4px'>
        Opcionais. Cada selo tem tipo, imagem e validade próprios. Sem upload,
        usa-se o modelo do tipo escolhido (ou o selo padrão do sistema).
       </Text>
      </Box>

      {/* Selo 1 */}
      <Box border='2px solid #EAEEF0' borderRadius='12px' p='20px'>
       <Heading as='h4' fontSize='15px' fontWeight={600} mb='16px'>
        Selo 1
       </Heading>
       <Box maxW='360px' mb='16px'>
        <Select
         label='Tipo do selo'
         value={sealModel}
         onChange={(e) => setSealModel(e.target.value)}
         options={SEAL_MODELS.map((m) => ({ label: m.label, value: m.value }))}
        />
       </Box>
       <SealAssetInput
        label='Imagem do selo 1'
        defaultUrl={modelUrl(sealModel) || defaultAssets?.seal?.url}
        onChange={setSeal}
       />
       <MonthRangeFields
        label='Validade do selo 1 (mês/ano)'
        from={validFromMonth}
        until={validUntilMonth}
        onFrom={setValidFromMonth}
        onUntil={setValidUntilMonth}
       />
      </Box>

      {/* Selo 2 (opcional) */}
      {!seal2Enabled ? (
       <Button
        variant='outline'
        alignSelf='flex-start'
        h='48px'
        px='24px'
        borderWidth='2px'
        borderStyle='dashed'
        borderColor='#0073BB'
        color='#0073BB'
        fontWeight={700}
        _hover={{ bg: '#EAF3FB' }}
        onClick={() => setSeal2Enabled(true)}
       >
        + Adicionar segundo selo
       </Button>
      ) : (
       <Box border='2px solid #EAEEF0' borderRadius='12px' p='20px'>
        <Flex justify='space-between' align='center' mb='16px'>
         <Heading as='h4' fontSize='15px' fontWeight={600}>
          Selo 2
         </Heading>
         <Button
          variant='ghost'
          size='sm'
          color='#EA582B'
          onClick={() => {
           setSeal2Enabled(false);
           setSeal2(null);
          }}
         >
          Remover
         </Button>
        </Flex>
        <Box maxW='360px' mb='16px'>
         <Select
          label='Tipo do selo'
          value={sealModel2}
          onChange={(e) => setSealModel2(e.target.value)}
          options={SEAL_MODELS.map((m) => ({ label: m.label, value: m.value }))}
         />
        </Box>
        <SealAssetInput
         label='Imagem do selo 2'
         defaultUrl={modelUrl(sealModel2) || defaultAssets?.seal?.url}
         hint='Sem upload, usa-se a arte do modelo (ou o selo padrão do sistema).'
         onChange={setSeal2}
        />
        <MonthRangeFields
         label='Validade do selo 2 (mês/ano)'
         from={validFromMonth2}
         until={validUntilMonth2}
         onFrom={setValidFromMonth2}
         onUntil={setValidUntilMonth2}
        />
       </Box>
      )}

      <SealAssetInput
       label='Guia de uso (PDF)'
       noun='guia'
       kind='file'
       accept='.pdf'
       defaultFilename={defaultAssets?.guide?.filename}
       hint='PDF com instruções de uso do selo.'
       onChange={setGuide}
      />
     </Stack>

     <HStack
      bg='white'
      mt='8px'
      justify='space-between'
      p='6'
      borderRadius='12px 12px 0 0'
      borderBottom='2px solid #F5F5FB'
     >
      <Heading as='h3' fontSize='md' fontWeight={600}>
       Questionários
      </Heading>
     </HStack>

     <Stack bg='white' spacing='0' p='6' borderRadius='0 0 12px 12px'>
      <HStack spacing={6} align='end'>
       <Select
        label='Questionário'
        placeholder='Selecione um questionário'
        options={questionnaireOptions}
        onChange={(e) => handleQuestionnaireSelect(e.target.value)}
        isDisabled={!!chosenQuestionnaire} // Desabilita o select após adicionar o questionário
       />

       <Button
        variant='primary'
        size='medium'
        w='137px'
        minW='137px'
        onClick={handleAddQuestionnaire}
        isDisabled={!selectedQuestionnaire || !!chosenQuestionnaire} // Desabilita o botão após adicionar
       >
        Adicionar
       </Button>
      </HStack>
     </Stack>

     {chosenQuestionnaire && (
      <Stack bg='white' spacing='6' p='6' borderRadius='0 0 12px 12px'>
       {!chosenQuestionnaire?.has_pillar ? (
        <Flex
         p='6'
         border='2px solid #EAEEF0'
         align='center'
         justify='space-between'
         gap='6'
         borderRadius='12px'
        >
         <Box fontWeight='600'>
          <Text lineHeight='28px'>{chosenQuestionnaire?.description}</Text>
          <Text as='span' fontSize='sm' color='#5C6066'>
           {chosenQuestionnaire?.total_questions} questões
          </Text>
         </Box>

         <Flex align='center' gap='4' ml='auto'>
          <Text fontSize='sm' color='#5C6066' fontWeight={600} whiteSpace='nowrap'>
           Nota de corte*
          </Text>
          <InputText
           placeholder='Digite a nota de corte para certificação'
           // value={cutoffScores.main}
           onChange={(e) => handleCutoffScoreChange(e.target.value)}
           type='number'
           min='0'
           w='304px'
           autoComplete='off'
          />
         </Flex>
         <ButtonOnlyIcon name='trash' h='32px' onClick={handleRemoveQuestionnaire} />
        </Flex>
       ) : (
        <Stack spacing='6' width='100%'>
         <Flex align='center' justify='space-between' pr='32px'>
          <Text fontWeight='bold' lineHeight='28px'>
           {chosenQuestionnaire?.description}
          </Text>
          <ButtonOnlyIcon name='trash' h='32px' onClick={handleRemoveQuestionnaire} />
         </Flex>

         <Stack spacing='12px'>
          {chosenQuestionnaire?.pillars?.map((pillar) => (
           <Flex
            key={pillar?.id_pillar}
            h='96px'
            p='6'
            border='2px solid #EAEEF0'
            align='center'
            justify='space-between'
            gap='6'
            borderRadius='12px'
           >
            <Box fontWeight='600'>
             <Text lineHeight='30px'>{pillar?.name_pillar}</Text>
             <Text as='span' fontSize='sm' color='#5C6066'>
              {pillar?.total_questions} questões
             </Text>
            </Box>

            <Flex align='center' gap='4' ml='auto'>
             <Text fontSize='sm' color='#5C6066' fontWeight={600} whiteSpace='nowrap'>
              Nota de corte*
             </Text>
             <InputText
              placeholder='Digite a nota de corte para certificação'
              // value={cutoffScores.main}
              onChange={(e) => handleCutoffScoreChange(e.target.value, pillar?.id_pillar)}
              type='number'
              min='0'
              w='304px'
              autoComplete='off'
             />
            </Flex>
           </Flex>
          ))}
         </Stack>
        </Stack>
       )}

       <Flex
        align='center'
        bg='rgba(234, 88, 43, 0.1)'
        h='43px'
        p='12px'
        borderRadius='8px'
       >
        <Text fontSize='sm' color='#EA582B'>
         *A <b>nota de corte</b> significa que a Empresa para estar apta para se
         certificar deverá ter uma nota <b>igual</b> ou <b>superior</b> ao valor definido
         {chosenQuestionnaire.has_pillar ? ' em cada um dos pilares' : ''}.
        </Text>
       </Flex>
      </Stack>
     )}

    </Container>
   </Center>
   <SuccessModal
    isOpen={isSubmitSuccessful}
    onClose={closeSuccessModal}
    title='Certificação criada com sucesso!'
   />
  </form>
 );
}
